import React from 'react';
import { Copy, CheckCircle, Share2, Linkedin, Mail } from 'lucide-react';

interface ReferralBoxProps {
  shareLink?: string;
  referralCode?: string;
  isMobile: boolean;
  copied: boolean;
  onCopy: () => void;
  onWhatsApp: () => void;
  onLinkedIn: () => void;
  onEmail: () => void;
}

export const ReferralBox: React.FC<ReferralBoxProps> = ({ 
  shareLink, 
  referralCode, 
  isMobile, 
  copied, 
  onCopy, 
  onWhatsApp,
  onLinkedIn,
  onEmail
}) => {
  const handleNativeShare = async () => {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: 'KaneaBourse Pro',
          text: `Rejoins-moi sur Kaneabourse ! Utilise mon code ${referralCode} pour obtenir 15 jours de Pro gratuitement.`,
          url: shareLink,
        });
      } catch (err) {
        console.error('Native share failed', err);
      }
    } else {
      onWhatsApp();
    }
  };

  return (
    <div className={`sponsorship-card main-card ${isMobile ? 'mobile-card' : ''}`}>
      {!isMobile && (
        <div className="card-icon code-icon">
          <Share2 size={24} />
        </div>
      )}
      <h3>Votre Lien de Parrainage</h3>
      <p className="card-subtitle">Partagez ce lien unique avec vos contacts</p>
      
      <div className="referral-box-modern">
        <input 
          type="text" 
          readOnly 
          value={shareLink || ''} 
          className="referral-input-field"
          onClick={(e) => (e.target as HTMLInputElement).select()}
        />
        <button 
          className={`copy-btn-modern ${copied ? 'copied' : ''}`} 
          onClick={onCopy}
          aria-label="Copier le lien"
        >
          {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
          <span>{copied ? 'Copié !' : 'Copier'}</span>
        </button>
      </div>

      <div className="share-suite-container">
        {/* Icon 1: WhatsApp */}
        <button className="share-suite-icon-btn whatsapp" onClick={onWhatsApp} title="Partager sur WhatsApp">
          <div className="icon-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M12.012 2C6.48 2 2 6.48 2 12.012c0 1.918.504 3.78 1.463 5.418L2 22l4.704-1.236c1.584.864 3.372 1.32 5.304 1.32 5.532 0 10.012-4.48 10.012-10.012S17.544 2 12.012 2zm0 18.372c-1.632 0-3.216-.432-4.608-1.26l-.324-.192-2.796.732.744-2.724-.216-.348c-.912-1.44-1.392-3.12-1.392-4.872 0-4.632 3.768-8.4 8.4-8.4 4.632 0 8.4 3.768 8.4 8.4-1.044 4.632-4.812 8.4-8.4 8.4zm4.848-6.624c-.264-.132-1.572-.78-1.812-.864-.24-.084-.42-.132-.588.132-.18.264-.672.864-.828 1.044-.156.18-.312.204-.576.072-.264-.132-1.128-.42-2.148-1.332-.792-.708-1.332-1.584-1.488-1.848-.156-.264-.012-.408.12-.54.12-.12.264-.312.396-.468.132-.156.18-.264.264-.444.084-.18.048-.336-.024-.468-.072-.132-.588-1.428-.804-1.956-.216-.516-.432-.444-.588-.456-.156-.012-.336-.012-.516-.012-.18 0-.468.072-.708.336-.24.264-.924.9-1.104 1.836-.18.936.492 1.848.564 1.944.072.096 1.344 2.052 3.252 2.88.456.192.816.312 1.092.396.456.144.876.12 1.2.072.36-.048 1.092-.444 1.248-.876.156-.432.156-.792.108-.876-.048-.084-.18-.132-.444-.264z"/>
            </svg>
          </div>
          <span className="icon-label">WhatsApp</span>
        </button>

        {/* Icon 2: LinkedIn */}
        <button className="share-suite-icon-btn linkedin" onClick={onLinkedIn} title="Partager sur LinkedIn">
          <div className="icon-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>
          </div>
          <span className="icon-label">LinkedIn</span>
        </button>

        {/* Icon 3: Email */}
        <button className="share-suite-icon-btn email" onClick={onEmail} title="Partager par Email">
          <div className="icon-wrapper">
            <Mail size={24} />
          </div>
          <span className="icon-label">Email</span>
        </button>

        {/* Icon 4: Other/Native Share */}
        <button className="share-suite-icon-btn other" onClick={handleNativeShare} title="Plus d'options">
          <div className="icon-wrapper">
            <Share2 size={24} />
          </div>
          <span className="icon-label">Plus d'options</span>
        </button>
      </div>
      
      <div className="referral-code-display">
        Code à utiliser : <strong>{referralCode}</strong>
        <span className="referral-micro-mention">(Le code est automatiquement appliqué via le lien)</span>
      </div>
    </div>
  );
};


interface StatCardProps {
  icon?: React.ReactNode;
  label: string;
  value: number | string;
  subValue?: string;
  variant?: 'default' | 'highlight';
  isMobile: boolean;
  isZeroState?: boolean;
}

export const StatCard: React.FC<StatCardProps> = ({ icon, label, value, subValue, variant = 'default', isMobile, isZeroState }) => {
  if (variant === 'highlight') {
    return (
      <div className={`sponsorship-card reward-card ${isMobile ? 'mobile-card' : ''} ${isZeroState ? 'zero-state' : ''}`}>
        <h3>{label}</h3>
        <div className="reward-value">
          <span>+{value}</span>
          <small>{subValue}</small>
        </div>
        <p>Gagnés grâce à vos amis</p>
      </div>
    );
  }

  return (
    <div className={`sponsorship-card stat-card ${isMobile ? 'mobile-card' : ''}`}>
      <div className="stat-info">
        <div className="stat-icon-wrapper">{icon}</div>
        <div>
          <span className="stat-label">{label}</span>
          <span className="stat-value">{value}</span>
        </div>
      </div>
    </div>
  );
};
